/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Grid, Paper, Popper } from '@mui/material';
import type { FC } from 'react';
import { AnimationLengthPanelConnect } from '../TimeSliderConnect/TimeSliderButtonsConnect/AnimationLengthPanelConnect';
import { AutoUpdateButtonConnect } from '../TimeSliderConnect/TimeSliderButtonsConnect/AutoUpdateButtonConnect';
import { BackwardForwardStepButtonConnect } from '../TimeSliderConnect/TimeSliderButtonsConnect/BackwardForwardStepButtonConnect';
import { NowButtonConnect } from '../TimeSliderConnect/TimeSliderButtonsConnect/NowButtonConnect';
import { PlayButtonConnect } from '../TimeSliderConnect/TimeSliderButtonsConnect/PlayButtonConnect';
import { SpeedButtonConnect } from '../TimeSliderConnect/TimeSliderButtonsConnect/SpeedButtonConnect';
import { TimeSpanButtonConnect } from '../TimeSliderConnect/TimeSliderButtonsConnect/TimeSpanButtonConnect';
import { TimeStepButtonConnect } from '../TimeSliderConnect/TimeSliderButtonsConnect/TimeStepButtonConnect';

const POPPER_WIDTH = 440;

export type OptionsMenuOrientation = 'horizontal' | 'vertical';

export const getOptionsMenuOrientation = (
  buttonElement: HTMLButtonElement | null,
  mapId: string,
): OptionsMenuOrientation => {
  const button = buttonElement?.getBoundingClientRect();
  const mapWindow = document
    .getElementById(`mapcontainer_${mapId}`)
    ?.getBoundingClientRect();
  if (!button || !mapWindow) {
    return 'horizontal';
  }
  const horizontalMenuFitsInsideMap =
    button.right - POPPER_WIDTH >= mapWindow.left;
  return horizontalMenuFitsInsideMap ? 'horizontal' : 'vertical';
};

export interface OptionsMenuProps {
  isOpen?: boolean;
  orientation: OptionsMenuOrientation;
  buttonElement: HTMLButtonElement | null;
  mapId: string;
}

export const OptionsMenu: FC<OptionsMenuProps> = ({
  isOpen = false,
  buttonElement,
  orientation,
  mapId,
}) => {
  if (!buttonElement) {
    return null;
  }
  const isVertical = orientation === 'vertical';

  return (
    <Popper
      anchorEl={buttonElement}
      data-testid="clockOptionsMenu"
      open={isOpen}
      placement="bottom-end"
      sx={{ zIndex: 1002 }}
    >
      <Paper elevation={3}>
        <Grid
          container
          spacing={0.5}
          sx={{
            padding: '8px',
            ...(isVertical && {
              '& button': { width: '100%!important' },
            }),
            flexDirection: isVertical ? 'column' : 'row',
          }}
        >
          <NowButtonConnect mapId={mapId} sourceId={mapId} />
          <AnimationLengthPanelConnect mapId={mapId} />
          <TimeSpanButtonConnect mapId={mapId} />
          <TimeStepButtonConnect mapId={mapId} />
          <SpeedButtonConnect mapId={mapId} />
          <AutoUpdateButtonConnect mapId={mapId} />
          <BackwardForwardStepButtonConnect
            isForwardStep={false}
            mapId={mapId}
          />
          <BackwardForwardStepButtonConnect
            isForwardStep={true}
            mapId={mapId}
          />
          <PlayButtonConnect mapId={mapId} />
        </Grid>
      </Paper>
    </Popper>
  );
};
